import React, { useEffect, useState, useRef } from 'react';
import ScrollPanel from './ScrollPanel';
import { ArrowRightIcon, PhoneIcon, MailIcon, MapPinIcon, ClockIcon, TruckIcon, HeartIcon, ChevronLeftIcon, ChevronRightIcon, Globe2Icon, BarChart3Icon, CheckCircleIcon, BoxIcon, ShieldIcon, BuildingIcon, CalendarIcon } from 'lucide-react';
import ScrollReveal from '../../components/ScrollReveal';


const HorizontalScrollSection = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [panelCount, setPanelCount] = useState(0);

  // rAF throttle flag
  const tickingRef = useRef(false);

  // helpers
  const getPanels = () =>
    Array.from(
      scrollContainerRef.current?.querySelectorAll<HTMLElement>(
        ":scope > .scroll-panel"
      ) ?? []
    );

  // Core: compute active by comparing panel centers to container center
  const computeActive = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const panels = getPanels();
    if (!panels.length) return;

    const center =
      container.scrollLeft + container.clientWidth / 2;

    let bestIdx = 0;
    let bestDist = Number.POSITIVE_INFINITY;

    for (let i = 0; i < panels.length; i++) {
      const el = panels[i];
      const elCenter = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(elCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }
    setActiveIndex(bestIdx);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // initialize count
    setPanelCount(getPanels().length);

    // scroll handler with rAF throttle (robust with trackpad inertia)
    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        computeActive();
        tickingRef.current = false;
      });
    };

    const onResize = () => {
      setPanelCount(getPanels().length);
      // recompute immediately so index stays correct
      computeActive();
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    // initial positioning (center first panel)
    const panels = getPanels();
    const first = panels[0];
    if (first && container) {
      const firstCenter = first.offsetLeft + first.offsetWidth / 2;
      const targetLeft = Math.max(0, firstCenter - container.clientWidth / 2);
      container.scrollTo({ left: targetLeft, behavior: "auto" });
    }
    // compute initial active
    computeActive();

    return () => {
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToPanel = (index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const panels = getPanels();
    const clamped = Math.max(0, Math.min(index, panels.length - 1));
    const el = panels[clamped];
    if (!el) return;
    const elCenter = el.offsetLeft + el.offsetWidth / 2;
    const targetLeft = Math.max(0, elCenter - container.clientWidth / 2);
    container.style.scrollBehavior = 'auto'; // evita doble suavizado por CSS
    container.scrollTo({ left: targetLeft, behavior: "smooth" });
  };

  const scrollLeft = () => scrollToPanel(activeIndex - 1);
  const scrollRight = () => scrollToPanel(activeIndex + 1);

  // Custom primary color
  const primaryColor = '#1db233';
  const darkBgColor = '#282a2a';
  return <div className="w-full overflow-hidden py-8 mb-10 relative" style={{
    backgroundColor: darkBgColor
  }}>


    <div className='text-center justify-center max-w-3xl mx-auto mb-8'>
      <ScrollReveal direction="fade" delay={0.2}>
        <h1 className="text-4xl font-encode font-bold text-[#ffffff] mb-4">
          Green Cube Logistics LLC

        </h1>
      </ScrollReveal>
      <p className="text-white/70 text-md">Our company in the USA!</p>
    </div>

    {/* Scroll navigation buttons */}
    <ScrollReveal direction="fade" delay={0.2}>
      <div className="hidden md:block">
        <button onClick={scrollLeft} className="absolute left-4 top-1/2 z-10 bg-black/30 hover:bg-black/50 p-3 rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 transform -translate-y-1/2 hover:scale-110 border border-white/10" style={{
          color: primaryColor
        }} aria-label="Scroll left">
          <ChevronLeftIcon className="h-6 w-6" />
        </button>
        <button onClick={scrollRight} className="absolute right-8 top-1/2 z-10 bg-black/30 hover:bg-black/50 p-3 rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 transform -translate-y-1/2 hover:scale-110 border border-white/10" style={{
          color: primaryColor
        }} aria-label="Scroll right">
          <ChevronRightIcon className="h-6 w-6" />
        </button>
      </div>
    </ScrollReveal>

    <ScrollReveal direction="fade" delay={0.2}>

      <div ref={scrollContainerRef} className="flex overflow-x-auto snap-x snap-proximity items-stretch scrollbar-hide no-scrollbar pb-16 gap-6 py-4 px-4 md:px-8 lg:px-12">
        {/* Header Panel - With dark theme and About Us content */}
        <div className="scroll-panel snap-center shrink-0 will-change-transform">
          <ScrollPanel
            className="relative overflow-hidden" // recorta el overlay con el mismo borde redondeado
            style={{
              color: 'white',

              backgroundImage: `
      linear-gradient(rgba(40,42,42,0.90), rgba(40,42,42,0.85)),
      url("/images/horizontal.jpg"),
      linear-gradient(135deg, #292a2a, #282a2a)
    `,
              backgroundSize: 'cover, cover, cover',
              backgroundPosition: 'center, center, center',
              backgroundRepeat: 'no-repeat, no-repeat, no-repeat'
            }}
            index={0}
            activeIndex={activeIndex}
            darkTheme={true}
          >

            <div className="flex flex-col h-full justify-center items-start relative z-10 text-left">
              <div className="mb-4">

                <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                  Green Cube <br />
                  Logistics{' '}
                  <span style={{
                    color: '#1db233'
                  }}>
                    LLC
                  </span>
                </h1>
                <p className="text-xl md:text-2xl mb-6 text-[#1db233] font-light">
                  International Vision
                </p>
              </div>
              {/* About us content integrated here */}
              <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl shadow-lg border border-white/5 mb-6">
                <BuildingIcon className="h-8 w-8 mb-3" style={{
                  color: primaryColor
                }} />
                <p className="text-gray-200 leading-relaxed">
                  "At Green Cube Logistics, we offer customized logistics
                  solutions, combining speed, efficiency, and a reliable network
                  of carriers. Our commitment is to provide exceptional service
                  while building enduring relationships with every customer we
                  serve."
                </p>
              </div>
              <a
                href="#contacto"
                className="bg-[#1db233] text-white font-semibold py-3.5 px-7 rounded-lg transition-all duration-300 flex items-center w-fit group shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Contact Us
                <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>
          </ScrollPanel>
        </div>
        {/* Our Services Panel - Glass effect */}
        <div className="scroll-panel snap-center shrink-0 will-change-transform">
          <ScrollPanel glassEffect={true} darkTheme={true} style={{
            background: 'linear-gradient(135deg, #373737ff, #3a3a3aff)'
          }} index={1} activeIndex={activeIndex} primaryColor={primaryColor}>
            <div className="h-full flex flex-col relative z-10 ">
              <div className="mb-6">

                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  Our Services
                </h2>
              </div>
              <div className="space-y-6 flex-grow text-left">
                <div className="p-5 rounded-xl bg-white/10 backdrop-blur-md hover:shadow-lg transition-all duration-300 border border-white/5 transform hover:-translate-y-1">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 shadow-sm" style={{
                      backgroundColor: `${primaryColor}30`
                    }}>
                      <TruckIcon className="h-6 w-6" style={{
                        color: primaryColor
                      }} />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      Full Truckload{' '}
                      <span className="text-sm ml-1 font-normal" style={{
                        color: primaryColor
                      }}>
                        (FTL)
                      </span>
                    </h3>
                  </div>
                  <p className="text-gray-300">
                    Trust us to handle your large shipments with precision,
                    efficiency, and cost-effectiveness.
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-white/10 backdrop-blur-md hover:shadow-lg transition-all duration-300 border border-white/5 transform hover:-translate-y-1">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 shadow-sm" style={{
                      backgroundColor: `${primaryColor}30`
                    }}>
                      <BoxIcon className="h-6 w-6" style={{
                        color: primaryColor
                      }} />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      Less Than Truckload{' '}
                      <span className="text-sm ml-1 font-normal" style={{
                        color: primaryColor
                      }}>
                        (LTL)
                      </span>
                    </h3>
                  </div>
                  <p className="text-gray-300">
                    Our expert team will optimize your logistics, providing secure
                    and efficient delivery for your goods while saving you time
                    and money.
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-white/10 backdrop-blur-md hover:shadow-lg transition-all duration-300 border border-white/5 transform hover:-translate-y-1">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 shadow-sm" style={{
                      backgroundColor: `${primaryColor}30`
                    }}>
                      <Globe2Icon className="h-6 w-6" style={{
                        color: primaryColor
                      }} />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      Cross Border{' '}
                      <span className="text-sm ml-1 font-normal" style={{
                        color: primaryColor
                      }}>
                        (CB)
                      </span>
                    </h3>
                  </div>
                  <p className="text-gray-300">
                    Expand your reach with our seamless cross-border solutions,
                    designed to move your goods smoothly and reliably across
                    international borders.
                  </p>
                </div>
              </div>
            </div>
          </ScrollPanel>
        </div>

        {/* Why Us Panel - Glass effect */}
        <div className="scroll-panel snap-center shrink-0 will-change-transform">
          <ScrollPanel glassEffect={true} darkTheme={true} style={{
            background: 'linear-gradient(135deg, #373737ff, #3a3a3aff)'
          }} index={2} activeIndex={activeIndex} primaryColor={primaryColor}>
            <div className="h-full flex flex-col relative z-10 ">
              <div className='mb-6'>
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  Why us?
                </h2>
              </div>
              <div className="space-y-5 flex-grow grid content-center text-left">
                <div className="p-4 rounded-xl bg-white/10 backdrop-blur-md hover:shadow-md transition-all duration-300 border border-white/5 transform hover:-translate-y-1">
                  <div className="flex items-start">
                    <div className="p-3 rounded-lg mr-4 flex-shrink-0 shadow-sm" style={{
                      backgroundColor: `${primaryColor}30`
                    }}>
                      <ClockIcon className="h-6 w-6" style={{
                        color: primaryColor
                      }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        Timely pick up
                      </h3>
                      <p className="text-gray-300">
                        We meet the agreed deadlines, every time.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-white/10 backdrop-blur-md hover:shadow-md transition-all duration-300 border border-white/5 transform hover:-translate-y-1">
                  <div className="flex items-start">
                    <div className="p-3 rounded-lg mr-4 flex-shrink-0 shadow-sm" style={{
                      backgroundColor: `${primaryColor}30`
                    }}>
                      <CheckCircleIcon className="h-6 w-6" style={{
                        color: primaryColor
                      }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        Reliable carriers
                      </h3>
                      <p className="text-gray-300">
                        We assign the right truck for every load, guaranteeing
                        safety and efficiency.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-white/10 backdrop-blur-md hover:shadow-md transition-all duration-300 border border-white/5 transform hover:-translate-y-1">
                  <div className="flex items-start">
                    <div className="p-3 rounded-lg mr-4 flex-shrink-0 shadow-sm" style={{
                      backgroundColor: `${primaryColor}30`
                    }}>
                      <ShieldIcon className="h-6 w-6" style={{
                        color: primaryColor
                      }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        Track your shipment
                      </h3>
                      <p className="text-gray-300">
                        Never out of sight, every load is tracked by satellite.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-white/10 backdrop-blur-md hover:shadow-md transition-all duration-300 border border-white/5 transform hover:-translate-y-1">
                  <div className="flex items-start">
                    <div className="p-3 rounded-lg mr-4 flex-shrink-0 shadow-sm" style={{
                      backgroundColor: `${primaryColor}30`
                    }}>
                      <HeartIcon className="h-6 w-6" style={{
                        color: primaryColor
                      }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        Customer support
                      </h3>
                      <p className="text-gray-300">
                        24/7 dedicated support for all your needs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollPanel>
        </div>

        {/* Our Resources Panel - Glass effect */}
        <div className="scroll-panel snap-center shrink-0 will-change-transform">
          <ScrollPanel
            glassEffect={true}
            darkTheme={true}
            style={{
              background: "linear-gradient(135deg, #373737ff, #3a3a3aff)",
            }}
            index={3}
            activeIndex={activeIndex}
            primaryColor={primaryColor}
          >
            <div className="h-full flex flex-col relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Our Resources
              </h2>

              {/* Flex principal con dos columnas iguales */}
              <div className="flex-grow flex flex-col md:flex-row items-stretch gap-8 text-left">
                {/* Columna izquierda: información */}
                <div className="flex-1 bg-white/10 backdrop-blur-md p-6 rounded-xl shadow-lg border border-white/5">
                  <BarChart3Icon
                    className="h-10 w-10 mb-4"
                    style={{ color: primaryColor }}
                  />
                  <ul className="space-y-4 text-gray-200">
                    {[
                      "Transport Management system",
                      "24/7 support",
                      "Experienced Logistics Operators",
                      "Reliable Carrier Network",
                      "Domestic and International Logistic Providers",
                    ].map((item, index) => (
                      <li
                        key={index}
                        className="flex items-center p-3 rounded-lg hover:bg-white/5 transition-colors duration-300"
                      >
                        <div
                          className="w-2 h-6 rounded-full mr-4"
                          style={{ backgroundColor: primaryColor }}
                        ></div>
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Columna derecha: imagen ocupa 50% */}
                <div className="flex-1 flex justify-center items-center">
                  <img
                    src="/images/resources.jpeg"
                    alt="Resources illustration"
                    className="
      w-full 
      object-cover 
      rounded-lg 
      shadow-md 
      h-40             /* altura pequeña en móviles */
      md:h-auto        /* altura automática en pantallas medianas+ */
      md:max-h-[450px] /* límite máximo solo en pantallas medianas+ */
    "
                  />
                </div>

              </div>
            </div>
          </ScrollPanel>
        </div>

        {/* Contact Us – combined section (Contact + Taglines) */}
        <div className="scroll-panel snap-center shrink-0 will-change-transform">
          <ScrollPanel
            glassEffect={true}
            darkTheme={true}
            style={{ background: "linear-gradient(135deg, #373737ff, #3a3a3aff)" }}
            index={4}
            activeIndex={activeIndex}
            primaryColor={primaryColor}
          >
            <div className="h-full flex flex-col relative z-10">
              <div className="mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-white">Contact Us</h2>
              </div>

              {/* Two-column layout: left = contact card, right = image with taglines */}
              <div className="flex-grow flex flex-col md:flex-row gap-6 md:gap-8 items-stretch text-left">
                {/* LEFT: Contact info */}
                <div className="flex-1">
                  <div className="bg-white/10 backdrop-blur-md p-4 md:p-6 rounded-xl shadow-lg border border-white/5 h-full">
                    <div className="space-y-4 md:space-y-5">
                      {/* Location */}
                      <div className="flex items-center p-3 rounded-lg hover:bg-white/5 transition-colors duration-300 group">
                        <div
                          className="w-11 h-11 p-3 rounded-lg mr-4 flex-shrink-0 shadow-sm group-hover:bg-opacity-40 transition-colors duration-300"
                          style={{ backgroundColor: `${primaryColor}30` }}
                        >
                          <MapPinIcon className="h-5 w-5" style={{ color: primaryColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight">LOCATION</p>
                          <p className="text-white font-bold text-base sm:text-lg leading-snug break-words">
                            8546 Broadway Ste 209 San Antonio TX 78217
                          </p>
                        </div>
                      </div>

                      {/* Phone */}
                      <div className="flex items-center p-3 rounded-lg hover:bg-white/5 transition-colors duration-300 group">
                        <div
                          className="w-11 h-11 p-3 rounded-lg mr-4 flex-shrink-0 shadow-sm group-hover:bg-opacity-40 transition-colors duration-300"
                          style={{ backgroundColor: `${primaryColor}30` }}
                        >
                          <PhoneIcon className="h-5 w-5" style={{ color: primaryColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight">PHONE</p>
                          <p className="text-white font-bold text-base sm:text-lg leading-snug break-words">
                            (210) 233 1213
                          </p>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex items-center p-3 rounded-lg hover:bg-white/5 transition-colors duration-300 group">
                        <div
                          className="w-11 h-11 p-3 rounded-lg mr-4 flex-shrink-0 shadow-sm group-hover:bg-opacity-40 transition-colors duration-300"
                          style={{ backgroundColor: `${primaryColor}30` }}
                        >
                          <MailIcon className="h-5 w-5" style={{ color: primaryColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight">EMAIL</p>
                          <p className="text-white font-bold text-base sm:text-lg leading-snug break-words">
                            logistics@greencubelog.com
                          </p>
                        </div>
                      </div>

                      {/* Hours */}
                      <div className="flex items-center p-3 rounded-lg hover:bg-white/5 transition-colors duration-300 group">
                        <div
                          className="w-11 h-11 p-3 rounded-lg mr-4 flex-shrink-0 shadow-sm group-hover:bg-opacity-40 transition-colors duration-300"
                          style={{ backgroundColor: `${primaryColor}30` }}
                        >
                          <CalendarIcon className="h-5 w-5" style={{ color: primaryColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-400 text-xs sm:text-sm leading-tight">HOURS</p>
                          <p className="text-white font-bold text-base sm:text-lg leading-snug break-words">
                            Monday–Friday: 8AM–6PM
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Background image with taglines */}
                <div className="flex-1 flex">
                  <div
                    className="
      relative overflow-hidden rounded-xl shadow-lg
      flex flex-col
      w-full h-full
    "
                    style={{
                      backgroundImage: `url("/images/commitment.jpeg")`,

                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    {/* Dark overlay */}
                    <div className="absolute inset-0 bg-[#074a11ff]/65" />

                    {/* Content */}
                    <div className="relative z-10 flex flex-col p-4 md:p-6 h-full">
                      <div className="hidden md:block w-12 h-1 bg-white/40 rounded-full mb-4" />
                      <h3 className="text-lg md:text-2xl font-bold text-white mb-3 md:mb-6">
                        Our Commitment
                      </h3>

                      <div className="space-y-3 md:space-y-4 mt-auto md:mt-0">
                        {[
                          "Our job is to make yours easier.",
                          "Leave it to us, we got it.",
                          "Commitment to excellence in every shipment.",
                        ].map((quote, i) => (
                          <div
                            key={i}
                            className="p-3 md:p-4 border-l-2 bg-white/10 backdrop-blur-sm rounded-r-xl hover:bg-white/15 transition-all duration-300"
                            style={{ borderColor: "rgba(255,255,255,0.35)" }}
                          >
                            <p className="text-sm md:text-xl text-green-100">"{quote}"</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollPanel>
        </div>
      </div>
    </ScrollReveal>

    {/* Progress bar moved to bottom */}
    <ScrollReveal direction="fade" delay={0.2}>

      <div className="max-w-xl mx-auto px-4 mb-10">
        <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: panelCount ? `${((activeIndex + 1) / panelCount) * 100}%` : '0%',
              backgroundColor: primaryColor
            }}
          />
        </div>
      </div>
    </ScrollReveal>

  </div>;
};
export default HorizontalScrollSection;